import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  CheckCircle as ProtectedIcon,
  Cancel as UnprotectedIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Timer as TimerIcon,
  Http as HttpIcon,
  Computer as ServerIcon
} from '@mui/icons-material';

const WAFScanner = () => {
  // Single scan state
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleScan = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}waf-scan/scan?url=${encodeURIComponent(url)}&verbose=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(`Scan failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getWAFStatusChip = (wafDetected, wafName) => {
    if (wafDetected) {
      return (
        <Chip
          icon={<ProtectedIcon />}
          label={`Protected by ${wafName || 'WAF'}`}
          color="success"
          size="large"
          sx={{ fontSize: '1rem', py: 2 }}
        />
      );
    } else {
      return (
        <Chip
          icon={<UnprotectedIcon />}
          label="No WAF Detected"
          color="error"
          size="large"
          sx={{ fontSize: '1rem', py: 2 }}
        />
      );
    }
  };

  const getConfidenceChip = (confidence) => {
    const color = confidence === 'High' ? 'success' : confidence === 'Medium' ? 'warning' : 'default';
    return (
      <Chip
        label={`${confidence} Confidence`}
        color={color}
        size="medium"
        sx={{ fontSize: '0.9rem' }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShieldIcon sx={{ fontSize: 28, color: '#667eea' }} />
          WAF Scanner
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Target Selection Card */}
      <Card sx={{ 
        mb: 3, 
        border: '1px solid #e0e0e0', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        }
      }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
          color: 'white',
          p: 2,
          borderRadius: '3px 3px 0 0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon sx={{ fontSize: 20 }} />
            Target Selection
          </Typography>
        </Box>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                  Target URL
                </Typography>
                <TextField
                  fullWidth
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      height: 56, // Fixed height to match button
                    },
                    '& .MuiInputBase-input': {
                      py: 0, // Remove extra padding to fit the fixed height
                      height: '56px',
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 3.5 }}> {/* Match the spacing from the label */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleScan}
                  disabled={loading || !url.trim()}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SecurityIcon />}
                  sx={{
                    height: 56, // Same height as TextField
                    borderRadius: 2, // Same border radius as TextField
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: 'rgba(0,0,0,0.12)',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? 'SCANNING...' : 'START WAF SCAN'}
                </Button>
                {!loading && !url.trim() && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    Enter a target URL to begin scanning
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

        {/* Results Section */}
        {result && (
          <Card sx={{ 
            mb: 4,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                fontWeight: 600,
                color: 'primary.main'
              }}>
                <ShieldIcon sx={{ mr: 1, fontSize: '1.8rem' }} />
                Scan Results
              </Typography>
              
              {/* Main Status Display */}
              <Box sx={{ 
                textAlign: 'center', 
                mb: 4, 
                p: 4, 
                background: result.waf_detected 
                  ? 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)'
                  : 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                borderRadius: 3,
                border: result.waf_detected ? '2px solid #4caf50' : '2px solid #f44336',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: result.waf_detected 
                    ? 'url("data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%234caf50" fill-opacity="0.1"%3E%3Cpath d="M10 2l3 6h5l-4 4 1.5 6L10 15l-5.5 3L6 12l-4-4h5z"/%3E%3C/g%3E%3C/svg%3E")'
                    : 'url("data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23f44336" fill-opacity="0.1"%3E%3Cpath d="M10 2l8 8-8 8-8-8z"/%3E%3C/g%3E%3C/svg%3E")',
                  opacity: 0.3
                }
              }}>
                <Typography variant="h6" gutterBottom sx={{ 
                  color: result.waf_detected ? 'success.dark' : 'error.dark',
                  fontWeight: 600,
                  position: 'relative',
                  zIndex: 1
                }}>
                  WAF Protection Status
                </Typography>
                <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
                  {getWAFStatusChip(result.waf_detected, result.waf_name)}
                </Box>
                <Typography variant="body1" sx={{ 
                  color: result.waf_detected ? 'success.dark' : 'error.dark',
                  fontWeight: 500,
                  position: 'relative',
                  zIndex: 1
                }}>
                  Target: <strong>{result.target_url}</strong>
                </Typography>
              </Box>

              {/* Detailed Results Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ 
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ 
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      '& .MuiTableCell-root': {
                        borderBottom: 'none'
                      }
                    }}>
                      <TableCell sx={{ 
                        color: 'white', 
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        py: 2
                      }}>Property</TableCell>
                      <TableCell sx={{ 
                        color: 'white', 
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        py: 2
                      }}>Value</TableCell>
                      <TableCell sx={{ 
                        color: 'white', 
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        py: 2
                      }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <TableCell sx={{ py: 2.5, fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ShieldIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                          WAF Status
                        </Box>
                      </TableCell>
                      <TableCell>
                        {result.waf_detected ? (
                          <Chip label="Protected" color="success" icon={<ProtectedIcon />} />
                        ) : (
                          <Chip label="Not Protected" color="error" icon={<UnprotectedIcon />} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{result.waf_name || 'No WAF detected'}</TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <TableCell sx={{ py: 2.5, fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <InfoIcon sx={{ mr: 1, color: 'info.main', fontSize: '1.2rem' }} />
                          Confidence Level
                        </Box>
                      </TableCell>
                      <TableCell>{getConfidenceChip(result.confidence_level)}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Detection accuracy level</TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <TableCell sx={{ py: 2.5, fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <HttpIcon sx={{ mr: 1, color: 'warning.main', fontSize: '1.2rem' }} />
                          Response Code
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={result.response_code || 'N/A'} 
                          color={result.response_code === 200 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>HTTP response status</TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <TableCell sx={{ py: 2.5, fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ServerIcon sx={{ mr: 1, color: 'secondary.main', fontSize: '1.2rem' }} />
                          Server Header
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {result.server_header || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Server technology information</TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <TableCell sx={{ py: 2.5, fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimerIcon sx={{ mr: 1, color: 'success.main', fontSize: '1.2rem' }} />
                          Scan Duration
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${result.scan_duration?.toFixed(2)}s`} 
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Time taken to complete scan</TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                      transition: 'background-color 0.2s ease'
                    }}>
                      <TableCell sx={{ py: 2.5, fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <SecurityIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                          Detection Method
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={result.detection_method} 
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Method used for WAF detection</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* HTTP Headers Details */}
              {result.waf_headers && Object.keys(result.waf_headers).length > 0 && (
                <Accordion sx={{ 
                  mt: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: 'rgba(99, 102, 241, 0.05)',
                      borderRadius: '8px 8px 0 0',
                      '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      <HttpIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                      HTTP Response Headers
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ 
                            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                            '& .MuiTableCell-root': {
                              borderBottom: 'none',
                              fontWeight: 600
                            }
                          }}>
                            <TableCell sx={{ py: 2, fontSize: '0.9rem' }}>Header Name</TableCell>
                            <TableCell sx={{ py: 2, fontSize: '0.9rem' }}>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(result.waf_headers).map(([key, value], index) => (
                            <TableRow key={key} sx={{ 
                              '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                              '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.05)' },
                              transition: 'background-color 0.2s ease'
                            }}>
                              <TableCell sx={{ 
                                py: 1.5, 
                                fontWeight: 500,
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                color: '#4338ca',
                                maxWidth: 200,
                                wordBreak: 'break-word'
                              }}>
                                {key}
                              </TableCell>
                              <TableCell sx={{ 
                                py: 1.5,
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                color: '#374151',
                                maxWidth: 400,
                                wordBreak: 'break-word'
                              }}>
                                {typeof value === 'string' && value.length > 100 
                                  ? `${value.substring(0, 100)}...` 
                                  : String(value)
                                }
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Detected Indicators */}
              {result.detected_indicators && result.detected_indicators.length > 0 && (
                <Accordion sx={{ 
                  mt: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: 'rgba(34, 197, 94, 0.05)',
                      borderRadius: '8px 8px 0 0',
                      '&:hover': { backgroundColor: 'rgba(34, 197, 94, 0.1)' }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                      Detection Indicators
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {result.detected_indicators.map((indicator, index) => (
                        <Chip
                          key={index}
                          label={indicator}
                          color="success"
                          variant="outlined"
                          size="small"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.8rem'
                          }}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </CardContent>
          </Card>
        )}

        {/* WAF Rules Information */}
        {result && result.waf_detected && (
          <Card sx={{ 
            mb: 4,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 3
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ShieldIcon sx={{ fontSize: 28 }} />
                WAF Rules Configuration
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Protection Level: {result.protection_level} | Score: {result.rules_protection_score}%
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 4 }}>
              {/* Rules Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {result.total_rules || 60}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Total Rules
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        All available WAF rules
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {result.enabled_rules_count}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Enabled Rules
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Active protection rules
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                        {result.disabled_rules_count}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Disabled Rules
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Not currently active
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Enabled Rules */}
              <Accordion defaultExpanded sx={{ 
                mb: 2,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                '&:before': { display: 'none' }
              }}>
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '8px 8px 0 0',
                    '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.15)' }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                    <ProtectedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Enabled Rules ({result.enabled_rules_count})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: '#f9fafb' }}>
                          <TableCell sx={{ fontWeight: 600, py: 2 }}>Rule ID</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 2 }}>Rule Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.enabled_rules && result.enabled_rules.map((rule, index) => (
                          <TableRow key={index} sx={{ 
                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                            '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.05)' }
                          }}>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#059669' }}>
                              {rule.rule_id}
                            </TableCell>
                            <TableCell>{rule.rule_name}</TableCell>
                            <TableCell>
                              <Chip label="Enabled" color="success" size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {/* Disabled Rules */}
              {result.disabled_rules && result.disabled_rules.length > 0 && (
                <Accordion sx={{ 
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  '&:before': { display: 'none' }
                }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      borderRadius: '8px 8px 0 0',
                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.15)' }
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                      <UnprotectedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Disabled Rules ({result.disabled_rules_count})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Rule ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Rule Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.disabled_rules.map((rule, index) => (
                            <TableRow key={index} sx={{ 
                              '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.05)' }
                            }}>
                              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#dc2626' }}>
                                {rule.rule_id}
                              </TableCell>
                              <TableCell>{rule.rule_name}</TableCell>
                              <TableCell>
                                <Chip label="Disabled" color="error" size="small" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* WAF Features */}
              {result.waf_features && result.waf_features.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    WAF Features
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {result.waf_features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        color="primary"
                        variant="outlined"
                        icon={<ShieldIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
  );
};

export default WAFScanner;
